import { apiPost, type RequestOptions } from '@shared/api/httpClient';

export type DuoInstallerUploadStatus = '' | 'UPLOADING' | 'SCANNING' | 'APPROVED' | 'BLOCKED' | 'FAILED';
export interface DuoInstallerUploadUrl {
  accountId?: string;
  uploadId: string;
  fileName: string;
  sizeBytes: number;
  sha256: string;
  quarantineS3Uri: string;
  uploadUrl: string;
  uploadFields: Record<string, string>;
  expiresIn?: number;
  status?: string;
  message?: string;
  storageMode?: string;
  bucket?: string;
}
export interface DuoInstallerUploadResult {
  accountId?: string;
  uploadId: string;
  fileName: string;
  version?: string;
  sizeBytes: number;
  sha256: string;
  quarantineS3Uri?: string;
  s3Uri?: string;
  versionId?: string;
  status: DuoInstallerUploadStatus;
  malwareScanStatus?: string;
  message?: string;
}

export async function uploadDuoInstaller(file: File, endpoint: string, options: {
  requestOptions?: Omit<RequestOptions, 'method' | 'body'>;
  minimumVersion?: string;
  onPrepared?: (upload: DuoInstallerUploadUrl) => void;
  onProgress?: (message: string) => void;
} = {}): Promise<DuoInstallerUploadResult> {
  if (!/^duoauthproxy.*\.exe$/i.test(file.name)) {
    throw new Error('Select the Windows Duo Authentication Proxy installer downloaded from Cisco Duo. The file name must begin with duoauthproxy and end in .exe.');
  }
  if (file.size < 1 || file.size > 250 * 1024 * 1024) {
    throw new Error('The Duo Authentication Proxy installer must be between 1 byte and 250 MB.');
  }
  const digest = await window.crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  const sha256 = Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  options.onProgress?.('Creating a secure upload request...');
  const upload = await apiPost<DuoInstallerUploadUrl>(`${endpoint}/upload-url`, { fileName: file.name, contentType: file.type || 'application/octet-stream', sizeBytes: file.size, sha256, ...(options.minimumVersion ? { minimumVersion: options.minimumVersion } : {}) }, options.requestOptions);
  const storageMode = String(upload.storageMode || '').trim().toUpperCase();
  const quarantineUri = String(upload.quarantineS3Uri || '').trim();
  const bucket = String(upload.bucket || '').trim() || quarantineUri.match(/^s3:\/\/([^/]+)\//)?.[1] || '';
  if (storageMode && storageMode !== 'CENTRAL_TOOLING_INTAKE'
    || !bucket.startsWith('surelinc-cmmc-client-package-intake-')
    || !quarantineUri.includes('/identity-artifacts/duo-installer/quarantine/')) {
    throw new Error('The upload service returned an unsafe installer destination. Deploy the central tooling intake and API changes before retrying.');
  }
  options.onPrepared?.(upload);
  options.onProgress?.('Uploading the installer for malware scanning...');
  const body = new FormData();
  Object.entries(upload.uploadFields || {}).forEach(([key, value]) => body.append(key, value));
  body.append('file', file);
  const response = await fetch(upload.uploadUrl, { method: 'POST', body });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    throw new Error(`S3 rejected the installer upload (${response.status}). ${detail.slice(0, 240)}`.trim());
  }
  return apiPost<DuoInstallerUploadResult>(`${endpoint}/complete`, {
    uploadId: upload.uploadId, fileName: upload.fileName, sizeBytes: upload.sizeBytes, sha256: upload.sha256,
  }, options.requestOptions);
}
